// FormContext.js
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from 'react';
import {
  getSavedForms,
  saveForm as saveFormService,
  deleteForm as deleteFormService,
} from '../services/formService'; // Adjust as needed

const FormContext = createContext();

export const FormProvider = ({ children }) => {
  // State for forms, submissions, loading, and error handling
  const [availableForms, setAvailableForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch forms with loading and error states (simulate async call)
  const fetchAvailableForms = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const forms = await getSavedForms();
      const processedForms = forms.map(form => ({
        ...form,
        title: form.title || form.name || 'Untitled Form',
        id: form.id || Math.random().toString(36).substr(2, 9),
      }));
      setAvailableForms(processedForms);
      if (processedForms.length > 0 && !selectedForm) {
        setSelectedForm(processedForms[0]);
      }
    } catch (err) {
      console.error('Error fetching forms:', err);
      setError('Failed to load forms. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedForm]);

  useEffect(() => {
    fetchAvailableForms();
  }, [fetchAvailableForms]);

  // Save a new submission or update an existing draft
  const saveSubmission = (newSubmission) => {
    setSubmissions(prev => {
      // Prevent changes if already submitted
      const submittedIndex = prev.findIndex(
        sub =>
          sub.formId === newSubmission.formId && sub.status === 'submitted'
      );
      if (submittedIndex !== -1) {
        return prev;
      }
      // Update draft if exists
      const draftIndex = prev.findIndex(
        sub =>
          sub.formId === newSubmission.formId && sub.status === 'draft'
      );
      if (draftIndex !== -1) {
        const updated = [...prev];
        updated[draftIndex] = newSubmission;
        return updated;
      }
      return [...prev, newSubmission];
    });
  };

  // Update an existing submission (e.g., change status from draft to submitted)
  const updateSubmission = (formId, updatedSubmission) => {
    setSubmissions(prev =>
      prev.map(sub =>
        sub.formId === formId ? { ...sub, ...updatedSubmission } : sub
      )
    );
  };

  // Remove a submission from state
  const removeSubmission = (formId) => {
    setSubmissions(prev => prev.filter(sub => sub.formId !== formId));
  };

  // Add a new form dynamically and update both state and persistence
  const addForm = async (newForm) => {
    try {
      const formToAdd = {
        ...newForm,
        title: newForm.title || newForm.name || 'Untitled Form',
        id: newForm.id || Math.random().toString(36).substr(2, 9),
      };
      // Save to external service (if applicable)
      await saveFormService(formToAdd);
      setAvailableForms(prev => [...prev, formToAdd]);
    } catch (err) {
      console.error('Error adding form:', err);
      setError('Failed to add form. Please try again.');
    }
  };

  // Update an existing form in state and persistence
  const updateForm = async (updatedForm) => {
    try {
      // Update in persistence service if available
      await saveFormService(updatedForm);
      setAvailableForms(prev =>
        prev.map(form => (form.id === updatedForm.id ? updatedForm : form))
      );
      if (selectedForm && selectedForm.id === updatedForm.id) {
        setSelectedForm(updatedForm);
      }
    } catch (err) {
      console.error('Error updating form:', err);
      setError('Failed to update form. Please try again.');
    }
  };

  // Remove a form from state and persistence
  const removeForm = async (formId) => {
    try {
      // Remove from external service if applicable
      await deleteFormService(formId);
      setAvailableForms(prev => prev.filter(form => form.id !== formId));
      if (selectedForm && selectedForm.id === formId) {
        setSelectedForm(prev => (prev.length > 0 ? prev[0] : null));
      }
    } catch (err) {
      console.error('Error deleting form:', err);
      setError('Failed to delete form. Please try again.');
    }
  };

  return (
    <FormContext.Provider
      value={{
        availableForms,
        selectedForm,
        setSelectedForm,
        fetchAvailableForms,
        submissions,
        setSubmissions,
        saveSubmission,
        updateSubmission,
        removeSubmission,
        addForm,
        updateForm,
        removeForm,
        isLoading,
        error,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};

export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
};
